/**
 * Bereiche eines Vorgangs, zwischen denen Dokumente verschoben werden koennen.
 * Die Schluessel entsprechen `targetKind` im Backend (`POST /document/move`).
 */
export type DocumentAreaKind =
  | 'certificate'
  | 'invoice'
  | 'invoiceObject'
  | 'draft'
  | 'other'
  | 'requirement'
  | 'directory';

/**
 * Konkrete Fundstelle eines Dokuments im Vorgang.
 *
 * `invoice` ist die Liste der nicht erkannten Rechnungen, `invoiceObject` eine
 * erkannte Rechnung (eigene Invoice-Entitaet). `invoiceObject` ist nur Herkunft,
 * niemals Ziel — beim Verschieben nach „Rechnungen" entscheidet die QR-Prüfung,
 * ob daraus eine erkannte Rechnung wird.
 */
export type IDocumentLocation =
  | { kind: 'certificate' }
  | { kind: 'invoice' }
  | { kind: 'invoiceObject' }
  | { kind: 'draft' }
  | { kind: 'other' }
  | { kind: 'requirement'; idLegalRequirement: number }
  | { kind: 'directory'; idDocumentDirectory: number };

/** Gruppen des Verschieben-Dialogs. */
export type DocumentMoveGroup = 'area' | 'requirement' | 'directory';

/** Ein auswaehlbares Ziel im Verschieben-Dialog. */
export interface IDocumentMoveTarget {
  location: IDocumentLocation;
  label: string;
  group: DocumentMoveGroup;
  /** Zwischenueberschrift innerhalb der Gruppe — bei Maßnahmen die Phase. */
  subGroup?: string;
  /** Einrueckungstiefe, damit die Ordnerstruktur sichtbar bleibt. */
  depth?: number;
  /** Zusatzzeile, z. B. der vollstaendige Ordnerpfad. */
  hint?: string;
}

export const DOCUMENT_MOVE_GROUP_LABEL: Record<DocumentMoveGroup, string> = {
  area: 'Vorgang',
  requirement: 'Abwicklung – Maßnahmen',
  directory: 'Interne Datenablage',
};

export const DOCUMENT_AREA_LABEL: Record<DocumentAreaKind, string> = {
  certificate: 'Urkunden',
  invoice: 'Rechnungen',
  invoiceObject: 'Rechnungen (erkannt)',
  draft: 'Entwürfe',
  other: 'Öffentliche Dokumente',
  requirement: 'Maßnahme',
  directory: 'Interne Datenablage',
};

export const isSameDocumentLocation = (
  a: IDocumentLocation,
  b: IDocumentLocation
): boolean => {
  if (a.kind !== b.kind) {
    return false;
  }

  if (a.kind === 'requirement' && b.kind === 'requirement') {
    return a.idLegalRequirement === b.idLegalRequirement;
  }

  if (a.kind === 'directory' && b.kind === 'directory') {
    return a.idDocumentDirectory === b.idDocumentDirectory;
  }

  return true;
};
